import { Component, computed, HostBinding, Input } from '@angular/core';
import { BaseInputComponent } from '../base-input/base-input.component';
import { DateInput } from '../date-input/date-input.component';
import { DateRangeInputConfig } from '../input.type';

/**
 * Daterange input component.
 * @param format - The format of the date.
 * @param minDate - The minimum date.
 * @param maxDate - The maximum date.
 */
@Component({
    selector: 'app-daterange-input',
    templateUrl: './daterange-input.component.html',
    providers: [{ provide: BaseInputComponent, useExisting: DaterangeInputComponent }]
})
export class DaterangeInputComponent extends BaseInputComponent<{ startDate: DateInput; endDate: DateInput }> {
    @Input() format: DateRangeInputConfig['format'] = 'dd. MM. yyyy';
    @Input() minDate: DateRangeInputConfig['minDate'];
    @Input() maxDate: DateRangeInputConfig['maxDate'];

    @HostBinding('class') hostClass = 'truncate';

    dateValue = computed<{ startDate: Date; endDate: Date }>(() => {
        if (!this.value()) return null;

        const startDateInput = this.value().startDate;
        const endDateInput = this.value().endDate;

        if (startDateInput instanceof Date && endDateInput instanceof Date) {
            return this.value() as { startDate: Date; endDate: Date };
        }

        if (startDateInput instanceof Date && typeof endDateInput === 'string') {
            const endDate = new Date(endDateInput);
            return isNaN(endDate.getTime()) ? null : { startDate: startDateInput as Date, endDate };
        }

        if (typeof startDateInput === 'string' && endDateInput instanceof Date) {
            const startDate = new Date(startDateInput);
            return isNaN(startDate.getTime()) ? null : { startDate, endDate: endDateInput as Date };
        }

        if (typeof startDateInput === 'string' && typeof endDateInput === 'string') {
            const startDate = new Date(startDateInput);
            const endDate = new Date(endDateInput);
            return isNaN(startDate.getTime()) || isNaN(endDate.getTime()) ? null : { startDate, endDate };
        }

        return null;
    });

    override onValueChange(value: { startDate: Date; endDate: Date }) {
        this.validate(value);
        this.dirty.set(true);
        this.valueChange.emit(value);
        this.value.set(value);
    }

    override validate(value: { startDate: Date; endDate: Date }) {
        const error: string[] = [];

        if (this.validateFn) {
            const validateResult = this.validateFn(value);
            if (validateResult) {
                error.push(validateResult);
            }
        }

        if (this.isRequired && (!value?.startDate || !value?.endDate)) {
            error.push(this.translationService.instant('This field is required'));
        }

        this.error.set(error.length > 0 ? error : null);
    }
}
